import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, GlobeIcon, SyncAltIcon, UsersIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useClientDomainPricing } from '../../../hooks/useClientDomainPricing';
import { formatPrice } from '../../../utils/money';
import type { DomainOperation, DomainTld } from '../../../types/models/DomainPricing';
import type { ApiResponseDomainPrice } from '../../../types/api/ApiResponseDomainPricing';
import DomainMatrixTable from '@app/DomainMatrixTable';
import DomainPriceCell, { type CellMutationResult } from './DomainPriceCell';

interface DomainClientOverridesTabProps {
  operations: DomainOperation[];
  /** Priceable (active) TLDs — the matrix rows. */
  tlds: DomainTld[];
}

// The per-client side of the matrix. It is impersonation-based: GET/PUT/DELETE /prices/domains/...
// resolve to the in-scope (impersonated) client via the X-Impersonate-Client-ID header, and writes are
// staff-only. So editing is offered only while impersonating; otherwise the tab prompts to impersonate.
function DomainClientOverridesTab({ operations, tlds }: DomainClientOverridesTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const user = useUserContext();
  const { target, isImpersonating } = useImpersonation();

  // Gate on the REAL user's staff flag (survives impersonation) plus an in-scope client to target.
  const canEdit = user?.is_ancalagon_staff === true && isImpersonating;

  const slugs = useMemo(() => operations.map((o) => o.slug), [operations]);
  const { priceMap, loading, error, refetch, applyClientCell } = useClientDomainPricing(slugs, isImpersonating);

  const saveOverride = async (op: DomainOperation, tld: DomainTld, unitPrice: string): Promise<CellMutationResult> => {
    try {
      const response = await apiFetch(`/prices/domains/${op.slug}/${tld.tld}`, {
        method: 'PUT',
        body: JSON.stringify({ unit_price: unitPrice }),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseDomainPrice | null;
      if (body?.status === 'success' && body.data?.price) {
        const price = body.data.price;
        applyClientCell(op.slug, tld.tld, { unit_price: price.unit_price, is_custom: price.is_custom });
        addNotification({
          title: 'Client price saved',
          srTitle: 'Success alert',
          description: `${op.label} for ${tld.label} is now ${formatPrice(price.unit_price)} (excl. VAT) for ${target?.name ?? 'this client'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return { ok: true, fieldError: null };
      }
      const fieldError = body?.data?.unit_price ?? null;
      addNotification({
        title: 'Could not save client price',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not save the client price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError };
    } catch (err) {
      addNotification({
        title: 'Could not save client price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  const clearOverride = async (op: DomainOperation, tld: DomainTld): Promise<{ ok: boolean }> => {
    try {
      const response = await apiFetch(`/prices/domains/${op.slug}/${tld.tld}`, { method: 'DELETE' });
      const body = (await response.json().catch(() => null)) as ApiResponseDomainPrice | null;
      if (body?.status === 'success') {
        // DELETE returns the now-effective default (is_custom false), or null when no default exists.
        const price = body.data?.price ?? null;
        applyClientCell(op.slug, tld.tld, price ? { unit_price: price.unit_price, is_custom: price.is_custom } : null);
        addNotification({
          title: 'Override removed',
          srTitle: 'Success alert',
          description: `${op.label} for ${tld.label} reverts to the platform default for ${target?.name ?? 'this client'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return { ok: true };
      }
      addNotification({
        title: 'Could not remove override',
        srTitle: 'Error alert',
        description: body?.message ?? 'Could not remove the override. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    } catch (err) {
      addNotification({
        title: 'Could not remove override',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false };
    }
  };

  if (!isImpersonating) {
    return (
      <EmptyState
        titleText="Impersonate a client to manage their domain overrides"
        icon={UsersIcon}
        variant="lg"
        headingLevel="h2"
      >
        <EmptyStateBody>
          Per-client domain prices are scoped to the client you&apos;re impersonating. Start impersonating a client
          (from the Customers page) to view and edit their domain price overrides; cleared cells revert to the
          platform default.
        </EmptyStateBody>
      </EmptyState>
    );
  }

  const renderCell = (op: DomainOperation, tld: DomainTld) => {
    const cell = priceMap.get(`${op.slug}:${tld.tld}`);
    return (
      <DomainPriceCell
        cellKey={`cli-${op.slug}-${tld.tld}`}
        unitPrice={cell?.unit_price ?? null}
        isCustom={cell?.is_custom ?? false}
        editable={canEdit}
        ariaContext={`${op.label} for ${tld.label}`}
        description={op.product_desc}
        onSave={(v) => saveOverride(op, tld, v)}
        onClear={() => clearOverride(op, tld)}
        canClear={cell?.is_custom ?? false}
        clearLabel="Reset to default"
      />
    );
  };

  const refreshButton = (
    <Button
      variant="secondary"
      icon={<SyncAltIcon />}
      onClick={() => void refetch()}
      isLoading={loading}
      isDisabled={loading}
    >
      Refresh
    </Button>
  );

  const renderMatrixArea = () => {
    if (loading && priceMap.size === 0) {
      return <CenteredSpinner />;
    }
    if (error && priceMap.size === 0) {
      return (
        <EmptyState
          titleText="Unable to load client domain prices"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    return (
      <>
        {/* A failed manual Refresh keeps the (stale) matrix visible — surface the error inline rather
            than silently leaving stale prices on screen (the cold-load failure uses the EmptyState above). */}
        {error && (
          <Alert
            variant="danger"
            isInline
            title="Could not refresh client domain prices"
            className="pf-v6-u-mb-md"
          >
            {error}
          </Alert>
        )}
        <DomainMatrixTable
          operations={operations}
          tlds={tlds}
          ariaLabel="Client domain price overrides matrix"
          renderCell={renderCell}
          toolbarActions={refreshButton}
          emptyState={
            <EmptyState titleText="No TLDs configured" icon={GlobeIcon} variant="lg" headingLevel="h2">
              <EmptyStateBody>No TLDs are available to price yet. Add one on the Default prices tab.</EmptyStateBody>
            </EmptyState>
          }
        />
      </>
    );
  };

  return (
    <>
      <Alert
        variant="info"
        isInline
        title={`Editing domain price overrides for ${target?.name ?? 'this client'}`}
        className="pf-v6-u-mb-md"
      >
        Prices set here apply only to this client; clearing a cell reverts it to the platform default.
      </Alert>
      {renderMatrixArea()}
    </>
  );
}

export default DomainClientOverridesTab;
